/**
 * URL state management hook for deals filters
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { DealCategory, SortOption, DealsFilters } from '../types';
import { CATEGORY_LABELS, SORT_OPTIONS, STORAGE_KEYS } from '../utils/constants';

const DEFAULT_FILTERS: DealsFilters = {
  category: 'all',
  country: '',
  storeId: '',
  search: '',
  sort: 'newest',
};

/**
 * Hook for managing deals filter state with URL persistence
 */
export function useDealsFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Parse filters from URL
  const filters = useMemo((): DealsFilters => {
    const category = (searchParams.get('category') || 'all') as DealCategory;
    const country = searchParams.get('country') || '';
    const storeId = searchParams.get('store') || '';
    const search = searchParams.get('search') || '';
    const sort = (searchParams.get('sort') || 'newest') as SortOption;

    // Validate category
    const validCategory = Object.keys(CATEGORY_LABELS).includes(category)
      ? category
      : 'all';

    // Validate sort
    const validSort = SORT_OPTIONS.some((opt) => opt.value === sort)
      ? sort
      : 'newest';

    return {
      category: validCategory,
      country,
      storeId,
      search,
      sort: validSort,
    };
  }, [searchParams]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 300);

    return () => clearTimeout(timer);
  }, [filters.search]);

  // Load stored country/store on mount if not in URL
  useEffect(() => {
    if (!filters.country) {
      const storedCountry = localStorage.getItem(STORAGE_KEYS.COUNTRY);
      const storedStore = localStorage.getItem(STORAGE_KEYS.STORE);

      if (storedCountry) {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('country', storedCountry);
          if (storedStore) {
            next.set('store', storedStore);
          }
          return next;
        }, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update a single filter
  const setFilter = useCallback(
    <K extends keyof DealsFilters>(key: K, value: DealsFilters[K]) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);

        if (value && value !== DEFAULT_FILTERS[key]) {
          next.set(key === 'storeId' ? 'store' : key, String(value));
        } else {
          next.delete(key === 'storeId' ? 'store' : key);
        }

        // Persist country/store to localStorage
        if (key === 'country' && value) {
          localStorage.setItem(STORAGE_KEYS.COUNTRY, String(value));
        }
        if (key === 'storeId') {
          if (value) {
            localStorage.setItem(STORAGE_KEYS.STORE, String(value));
          } else {
            localStorage.removeItem(STORAGE_KEYS.STORE);
          }
        }

        // Reset store when country changes
        if (key === 'country') {
          next.delete('store');
          localStorage.removeItem(STORAGE_KEYS.STORE);
        }

        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  // Set multiple filters at once
  const setFilters = useCallback(
    (updates: Partial<DealsFilters>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);

        Object.entries(updates).forEach(([key, value]) => {
          const paramKey = key === 'storeId' ? 'store' : key;

          if (value && value !== DEFAULT_FILTERS[key as keyof DealsFilters]) {
            next.set(paramKey, String(value));
          } else {
            next.delete(paramKey);
          }

          // Persist to localStorage
          if (key === 'country' && value) {
            localStorage.setItem(STORAGE_KEYS.COUNTRY, String(value));
          }
          if (key === 'storeId') {
            if (value) {
              localStorage.setItem(STORAGE_KEYS.STORE, String(value));
            } else {
              localStorage.removeItem(STORAGE_KEYS.STORE);
            }
          }
        });

        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  // Reset all filters
  const resetFilters = useCallback(() => {
    const country = filters.country; // Keep country
    setSearchParams(
      country ? { country } : {},
      { replace: true }
    );
  }, [filters.country, setSearchParams]);

  // Get shareable URL
  const getShareableUrl = useCallback(() => {
    const params = new URLSearchParams();

    if (filters.category !== 'all') {
      params.set('category', filters.category);
    }
    if (filters.country) {
      params.set('country', filters.country);
    }
    if (filters.storeId) {
      params.set('store', filters.storeId);
    }
    if (filters.search) {
      params.set('search', filters.search);
    }
    if (filters.sort !== 'newest') {
      params.set('sort', filters.sort);
    }

    const queryString = params.toString();
    return queryString ? `/deals?${queryString}` : '/deals';
  }, [filters]);

  return {
    filters,
    debouncedSearch,
    setFilter,
    setFilters,
    resetFilters,
    getShareableUrl,
  };
}

/**
 * Hook for detecting and setting initial country
 */
export function useInitialCountry(
  detectedCountry: string | undefined,
  availableCountries: string[] | undefined,
  setFilter: (key: 'country', value: string) => void,
  currentCountry: string
) {
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (hasInitialized || currentCountry) return;

    // Try localStorage first
    const storedCountry = localStorage.getItem(STORAGE_KEYS.COUNTRY);
    if (storedCountry && availableCountries?.includes(storedCountry)) {
      // Intentionally setting filter when dependencies are satisfied
      setFilter('country', storedCountry);
      setHasInitialized(true);
      return;
    }

    // Then try detected country
    if (detectedCountry && availableCountries?.includes(detectedCountry)) {
      setFilter('country', detectedCountry);
      setHasInitialized(true);
      return;
    }

    // Fall back to first available country
    if (availableCountries?.length) {
      setFilter('country', availableCountries[0]);
      setHasInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    detectedCountry,
    availableCountries,
    currentCountry,
    hasInitialized,
  ]);

  return hasInitialized || Boolean(currentCountry);
}
